import type { ResponseData } from '@vrplatform/data';
import { type Maybe, day, sortBy, toTitleCase } from '@vrplatform/utils';

export type GuestBalancesTableQuerySorting =
  | 'guestName'
  | 'checkIn'
  | 'checkOut'
  | 'centTotal';

type ApiRow = ResponseData<'get:/reports/guest-balances'>['data'][0];
type ApiReservation = ApiRow['listings'][number]['reservations'][number] & {
  transactionDate?: Maybe<string>;
  transactionDescription?: Maybe<string>;
};

type Base = {
  id: string;
  name: string;
  centTotal: number;
  chargesCentTotal: number;
  paymentsCentTotal: number;
};

type Reservation = {
  guestName: string;
  checkIn: Maybe<string>;
  checkOut: Maybe<string>;
  cancelledAt: Maybe<string>;
  bookingPlatform: string;
  nights: number;
  bookedAt: Maybe<string>;
  confirmationCode: string;
  bookingStatus: ApiRow['status'];
  transactionDate: Maybe<string>;
  transactionDescription: Maybe<string>;
  type: 'reservation';
};

type Listing = {
  type: 'listing';
};

type Status = {
  type: 'status';
};

export type GuestBalancesTableQueryRow = Base &
  (Reservation | Listing | Status) & {
    subRows: GuestBalancesTableQueryRow[];
  };

export const formatGuestBalancesRow = (
  row: ApiRow & {
    listings: Array<
      ApiRow['listings'][number] & {
        reservations: ApiReservation[];
      }
    >;
  },
  sorting: GuestBalancesTableQuerySorting
): GuestBalancesTableQueryRow => {
  return {
    id: row.status,
    centTotal: row.total,
    chargesCentTotal: row.charges,
    paymentsCentTotal: row.payments,
    name: toTitleCase(row.status),
    type: 'status',
    subRows: sortBy(
      row.listings.map((listing) => ({
        id: `${row.status}-${listing.id}`,
        name: listing.name,
        type: 'listing',
        centTotal: listing.total,
        chargesCentTotal: listing.charges,
        paymentsCentTotal: listing.payments,
        subRows: sortBy(
          listing.reservations.map((reservation) => {
            const reservationWithTransactionFields =
              reservation as ApiReservation;
            const transactionDate =
              reservationWithTransactionFields.transactionDate
                ? day(
                    reservationWithTransactionFields.transactionDate
                  ).yyyymmdd()
                : null;
            const displayDate =
              row.status === 'unassigned'
                ? transactionDate
                : reservation.checkOut
                  ? day(reservation.checkOut).yyyymmdd()
                  : null;

            return {
              id: reservation.id,
              name:
                row.status === 'unassigned'
                  ? (reservationWithTransactionFields.transactionDescription ??
                    '')
                  : reservation.guestName,
              guestName:
                row.status === 'unassigned'
                  ? (reservationWithTransactionFields.transactionDescription ??
                    '')
                  : reservation.guestName,
              bookingPlatform: reservation.bookingPlatform,
              bookingStatus: row.status,
              cancelledAt: reservation.cancelledAt
                ? day(reservation.cancelledAt).yyyymmdd()
                : null,
              checkIn:
                row.status === 'unassigned'
                  ? transactionDate
                  : reservation.checkIn
                    ? day(reservation.checkIn).yyyymmdd()
                    : null,
              checkOut: displayDate,
              nights: reservation.nights,
              bookedAt: reservation.bookedAt
                ? day(reservation.bookedAt).yyyymmdd()
                : null,
              confirmationCode: reservation.confirmationCode,
              transactionDate,
              transactionDescription:
                reservationWithTransactionFields.transactionDescription ?? null,
              type: 'reservation',
              centTotal: reservation.total,
              chargesCentTotal: reservation.charges,
              paymentsCentTotal: reservation.payments,
              subRows: [],
            };
          }),
          sorting,
          sorting === 'centTotal' ? 'desc' : 'asc'
        ),
      })),
      'name'
    ),
  };
};
